/* =====================================================================
 * Token exhaustion domain helper
 *
 * Resolves the appropriate UX response when an agent stream request is
 * attempted while the caller's remaining tokens are <= 0. For personal
 * scope we check whether billing is in trialing state (which should open
 * the upgrade modal); otherwise we fall back to a toast notification.
 *
 * Billing status is read through a host-registered
 * `BillingCacheAdapter` (see `runtime/billingCacheAdapter.ts`). This
 * keeps @auw/stores independent of React Query and lets B2B tenants
 * opt out of billing entirely (no adapter → always falls back to a
 * toast and never tries to open the upgrade modal).
 * ===================================================================== */

import type { BillingStatus } from '@auw/api-client';
import i18n from '@auw/i18n';
import { getBillingCacheAdapter } from '../../runtime/billingCacheAdapter';
import { useUIStore } from '../../uiStore';

export type TokenExhaustionScope = 'personal' | 'team';

export function handleTokensExhausted(scope: TokenExhaustionScope): void {
  if (scope !== 'personal') {
    showTokenExhaustedToast(scope);
    return;
  }

  const adapter = getBillingCacheAdapter();
  if (!adapter) {
    // No billing layer registered (e.g. on-prem B2B without billing);
    // degrade gracefully to a plain toast.
    showTokenExhaustedToast(scope);
    return;
  }

  const cached = adapter.get();
  if (cached) {
    applyTokenExhaustedAction(cached, scope);
    return;
  }

  adapter
    .fetch()
    .then((fresh) => {
      applyTokenExhaustedAction(fresh, scope);
    })
    .catch(() => {
      showTokenExhaustedToast(scope);
    });
}

function applyTokenExhaustedAction(billing: BillingStatus, scope: TokenExhaustionScope): void {
  if (scope === 'personal' && billing.billing_status === 'trialing') {
    useUIStore.getState().openTokenExhaustedModal(billing.trial_ends_at ?? null);
  } else {
    showTokenExhaustedToast(scope);
  }
}

function showTokenExhaustedToast(scope: TokenExhaustionScope): void {
  useUIStore.getState().addToast({
    type: 'warning',
    title: i18n.t('common:error.tokensExhausted'),
    description:
      scope === 'team'
        ? i18n.t('common:error.teamTokensExhausted')
        : i18n.t('common:error.personalTokensExhausted'),
  });
}
